import { Area, AreaChart, Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { compactMoney, money, monthName, shortMonth } from "@/lib/books";

/**
 * Dashboard charts. Colours come from the validated two-slot categorical pair
 * (blue #2a78d6 / orange #eb6834 — adjacent CVD ΔE 24.7, normal 33.6 on white)
 * and the status "serious" orange for overdue. Text never wears series colour.
 */
export const SERIES_1 = "#2a78d6";
export const SERIES_2 = "#eb6834";
export const STATUS_SERIOUS = "#ec835a";
const GRID = "#e1e0d9";
const AXIS = "#c3c2b7";
const MUTED = "#898781";

const axisTick = { fill: MUTED, fontSize: 11 };

/** Month on the axis with its year underneath: "Apr" / "2026". */
function MonthTick({ x, y, payload }) {
  return (
    <text x={x} y={y + 12} textAnchor="middle" fill={MUTED} fontSize={11}>
      <tspan x={x}>{monthName(payload.value)}</tspan>
      <tspan x={x} dy={13} fontSize={10}>{payload.value.slice(0, 4)}</tspan>
    </text>
  );
}

function TooltipCard({ active, payload, label, rows }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-slate-200 bg-white px-3 py-2 text-xs shadow-md">
      <div className="mb-1 text-slate-500">{shortMonth(label)}</div>
      {rows.map(([key, name, color]) => {
        const item = payload.find((p) => p.dataKey === key);
        if (!item) return null;
        return (
          <div key={key} className="flex items-center gap-2">
            <span className="inline-block h-0.5 w-3 rounded" style={{ background: color }} />
            <span className="font-semibold text-slate-900">{money(item.value)}</span>
            <span className="text-slate-500">{name}</span>
          </div>
        );
      })}
    </div>
  );
}

/** Month-end cash + bank balance through the year: one series, crosshair
 * tooltip. Months still to come carry no value, so the line ends at today. */
export function CashBalanceChart({ months }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <AreaChart data={months} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="month" interval={0} tick={<MonthTick />} height={36} tickLine={false} axisLine={{ stroke: AXIS }} />
        <YAxis tickFormatter={compactMoney} tick={axisTick} tickLine={false} axisLine={false} width={60} />
        <Tooltip cursor={{ stroke: AXIS, strokeWidth: 1 }}
          content={<TooltipCard rows={[["closing", "cash at month end", SERIES_1]]} />} />
        <Area type="monotone" dataKey="closing" baseValue="dataMin" stroke={SERIES_1} strokeWidth={2} fill={SERIES_1} fillOpacity={0.1}
          dot={false} activeDot={{ r: 4, stroke: "#fff", strokeWidth: 2 }} isAnimationActive={false} />
      </AreaChart>
    </ResponsiveContainer>
  );
}

/** Income vs expense per month: two series, grouped columns, legend kept in the card header. */
export function IncomeExpenseChart({ months }) {
  return (
    <ResponsiveContainer width="100%" height={240}>
      <BarChart data={months} margin={{ top: 8, right: 16, bottom: 0, left: 0 }} barGap={2} barCategoryGap="28%">
        <CartesianGrid vertical={false} stroke={GRID} />
        <XAxis dataKey="month" interval={0} tick={<MonthTick />} height={36} tickLine={false} axisLine={{ stroke: AXIS }} />
        <YAxis tickFormatter={compactMoney} tick={axisTick} tickLine={false} axisLine={false} width={60} />
        <Tooltip cursor={{ fill: "rgba(11,11,11,0.04)" }}
          content={<TooltipCard rows={[["income", "income", SERIES_1], ["expense", "expense", SERIES_2]]} />} />
        <Bar dataKey="income" fill={SERIES_1} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
        <Bar dataKey="expense" fill={SERIES_2} radius={[4, 4, 0, 0]} maxBarSize={24} isAnimationActive={false} />
      </BarChart>
    </ResponsiveContainer>
  );
}

/** Legend entry: a swatch beside ink-coloured text. */
export function Key({ color, label, value, shape = "rect" }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-sm text-slate-600">
      <span className={shape === "line" ? "inline-block h-0.5 w-3 rounded" : "inline-block h-2.5 w-2.5 rounded-sm"}
        style={{ background: color }} />
      {label}
      {value !== undefined && <span className="font-semibold text-slate-900">{value}</span>}
    </span>
  );
}

/** Current vs overdue: a two-segment bar with a 2px surface gap between segments. */
export function SplitBar({ current, overdue }) {
  const total = Math.max(current + overdue, 0);
  const cur = total ? Math.max(current, 0) / total : 0;
  return (
    <div className="flex h-2.5 w-full gap-[2px] overflow-hidden rounded bg-slate-100" role="img"
      aria-label={`Current ${money(current)}, overdue ${money(overdue)}`}>
      {current > 0 && <div className="h-full rounded-l" style={{ width: `${cur * 100}%`, background: SERIES_1 }} />}
      {overdue > 0 && <div className="h-full rounded-r" style={{ width: `${(1 - cur) * 100}%`, background: STATUS_SERIOUS }} />}
    </div>
  );
}

/** Ranked horizontal bars for a short list (top expenses). One hue, value at the tip. */
export function RankedBars({ rows, onClick }) {
  const max = Math.max(...rows.map((r) => r.amount), 0) || 1;
  return (
    <div className="space-y-2.5">
      {rows.map((r) => (
        <button key={r.name} onClick={r.guid && onClick ? () => onClick(r) : undefined}
          className={`block w-full text-left ${r.guid && onClick ? "group" : "cursor-default"}`}>
          <div className="mb-1 flex justify-between gap-3 text-sm">
            <span className="truncate text-slate-700 group-hover:text-blue-700">{r.name}</span>
            <span className="shrink-0 font-medium tabular-nums text-slate-900">{money(r.amount)}</span>
          </div>
          <div className="h-2 rounded-r bg-transparent">
            <div className="h-full rounded-r" style={{ width: `${(r.amount / max) * 100}%`, background: SERIES_1, minWidth: 4 }} />
          </div>
        </button>
      ))}
    </div>
  );
}
